// Admin → Testimonials + Build Shares tabs (Phase 5 admin)
// Compact reusable list with status select (New/Approved/Featured/Archived) + admin notes.
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Save, RefreshCw, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPatch } from "@/lib/api";

const STATUS_STYLES = {
  "New": "border-[var(--line)] text-ink/65 bg-white",
  "Approved": "border-sky-300 text-sky-800 bg-sky-50",
  "Featured": "border-brand/40 text-brand bg-brand-soft",
  "Archived": "border-[var(--line)] text-ink/45 bg-bone",
};

function FollowupListTab({ pw, kind }) {
  // kind: "testimonials" | "build-shares"
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], statuses: [] });
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState({ status: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminFetch(`/admin/lab-followup/${kind}`, pw);
      setData(d);
    } catch { toast.error(`Failed to load ${kind}`); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  const openDetail = (row) => {
    setSelected(row);
    setEditing({ status: row.status || "New", admin_notes: row.admin_notes || "" });
  };

  const saveDetail = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await adminPatch(`/admin/lab-followup/${kind}/${selected.id}`, pw, editing);
      toast.success("Saved.");
      setSelected(res.row);
      await load();
    } catch (e) { toast.error(e?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const label = kind === "testimonials" ? "Testimonials" : "Build Shares";

  return (
    <div className="space-y-5" data-testid={`${kind}-tab`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">{kind === "testimonials" ? "Social Proof" : "Community"}</div>
          <h2 className="font-display text-2xl tracking-[-0.025em]">{label}</h2>
          <p className="text-sm text-ink/60 mt-1">
            {kind === "testimonials"
              ? "Written and video testimonials submitted via the follow-up hub. Mark Featured to show on the public landing."
              : "Builds shared by participants. Mark Featured to surface them on the public landing showcase."}
          </p>
        </div>
        <Button variant="outline" onClick={load} className="btn-outline-ink rounded-full h-10 px-4"><RefreshCw size={13} className="mr-1.5" />Refresh</Button>
      </div>

      <div className="rounded-2xl border border-[var(--line)] bg-bone overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-ink/50"><Loader2 className="inline mr-2 animate-spin" size={14} />Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="py-12 text-center text-ink/50">No {label.toLowerCase()} yet. New submissions land here automatically.</div>
        ) : (
          <ul className="divide-y divide-[var(--line)]">
            {data.rows.map((row) => (
              <li
                key={row.id}
                onClick={() => openDetail(row)}
                data-testid={`${kind}-row-${row.id}`}
                className="px-5 py-4 cursor-pointer hover:bg-white flex items-start justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink">{row.participant_name}</span>
                    <Badge variant="outline" className={STATUS_STYLES[row.status] || STATUS_STYLES["New"]}>{row.status || "New"}</Badge>
                    {kind === "testimonials" && (
                      <Badge variant="outline" className="border-[var(--line)] text-ink/55 bg-white text-[10px]">{row.testimonial_type}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-ink/70 mt-1 line-clamp-2">
                    {kind === "testimonials"
                      ? (row.written_testimonial || row.video_url || "—")
                      : (row.build_title + " — " + (row.description || ""))}
                  </p>
                  <p className="text-xs text-ink/45 mt-1 font-mono-spec">{row.email}{row.business_name ? ` · ${row.business_name}` : ""} · {new Date(row.submitted_at).toLocaleDateString()}</p>
                </div>
                <Button size="sm" variant="outline" className="btn-outline-ink rounded-full h-8 px-3 text-xs">View</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-[-0.025em]">{selected.participant_name}</DialogTitle>
                <DialogDescription>Submitted {new Date(selected.submitted_at).toLocaleString()}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="text-sm text-ink/75"><strong>Email:</strong> <a href={`mailto:${selected.email}`} className="text-brand underline">{selected.email}</a></div>
                {selected.business_name && <div className="text-sm text-ink/75"><strong>Business:</strong> {selected.business_name}</div>}
                {kind === "testimonials" ? (
                  <>
                    {selected.written_testimonial && (
                      <div><div className="eyebrow !mb-1.5">Written testimonial</div>
                        <div className="rounded-xl border border-[var(--line)] bg-bone p-3 text-sm whitespace-pre-wrap">{selected.written_testimonial}</div></div>
                    )}
                    {selected.video_url && (
                      <div><div className="eyebrow !mb-1.5">Video URL</div>
                        <a href={selected.video_url} target="_blank" rel="noopener noreferrer" className="text-brand underline break-all">{selected.video_url} <ExternalLink size={11} className="inline" /></a></div>
                    )}
                  </>
                ) : (
                  <>
                    <div><div className="eyebrow !mb-1.5">Build title</div><div className="text-ink font-medium">{selected.build_title}</div></div>
                    {selected.build_link && <div><div className="eyebrow !mb-1.5">Live link</div><a href={selected.build_link} target="_blank" rel="noopener noreferrer" className="text-brand underline break-all">{selected.build_link} <ExternalLink size={11} className="inline" /></a></div>}
                    {selected.screenshot_url && <div><div className="eyebrow !mb-1.5">Screenshot</div><a href={selected.screenshot_url} target="_blank" rel="noopener noreferrer" className="text-brand underline break-all">{selected.screenshot_url} <ExternalLink size={11} className="inline" /></a></div>}
                    <div><div className="eyebrow !mb-1.5">Description</div><div className="rounded-xl border border-[var(--line)] bg-bone p-3 text-sm whitespace-pre-wrap">{selected.description}</div></div>
                  </>
                )}
                <div className="text-xs text-ink/55">Permission to use: {selected.permission_granted ? "✓ Granted" : "✗ Not granted"}</div>

                <div>
                  <div className="eyebrow !mb-1.5">Status</div>
                  <Select value={editing.status} onValueChange={(v) => setEditing((s) => ({ ...s, status: v }))}>
                    <SelectTrigger className="bg-bone border border-[var(--line)]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-ink/55 mt-1.5">Mark <em>Featured</em> to surface on the public landing showcase (requires permission_granted).</p>
                </div>
                <div>
                  <div className="eyebrow !mb-1.5">Admin notes</div>
                  <Textarea rows={3} value={editing.admin_notes} onChange={(e) => setEditing((s) => ({ ...s, admin_notes: e.target.value }))} className="bg-bone border border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0" />
                </div>
              </div>
              <DialogFooter className="pt-4 gap-2">
                <Button variant="outline" onClick={() => setSelected(null)} className="btn-outline-ink rounded-full h-10 px-5">Close</Button>
                <Button onClick={saveDetail} disabled={saving} className="btn-ink rounded-full h-10 px-5">{saving ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} className="mr-1.5" />Save</>}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function TestimonialsAdmin({ pw }) { return <FollowupListTab pw={pw} kind="testimonials" />; }
export function BuildSharesAdmin({ pw }) { return <FollowupListTab pw={pw} kind="build-shares" />; }
